import { isFullCommitSha, resolveBuildCommitFromEnv } from "@cmmd-forge/shared/buildCommit";

/**
 * Resolves the Sentry DSN inlined into the web bundle at build time.
 *
 * Vite only exposes `VITE_`-prefixed variables to client code, so a deploy that
 * sets a plain `SENTRY_DSN` builds a bundle with an empty DSN and reports
 * nothing. That is not hypothetical: Forge Cloud ran with `SENTRY_DSN` set
 * correctly on the Railway service and a Shell that never sent a single event,
 * which is why a crash on EVERY turn completion went unseen.
 *
 * Accepting the unprefixed name removes the trap rather than documenting it.
 */
export function resolveSentryDsn(env: Record<string, string | undefined>): string {
  return (env.VITE_SENTRY_DSN ?? env.SENTRY_DSN ?? "").trim();
}

/**
 * A production build with no DSN cannot report errors, and silence from a
 * monitoring system is indistinguishable from health. Callers surface this so
 * the gap shows up in build logs instead of during an incident.
 */
export function describeMissingProductionDsn(input: {
  readonly dsn: string;
  readonly mode: string;
}): string | null {
  if (input.mode !== "production") return null;
  if (input.dsn !== "") return null;
  return "No Sentry DSN resolved for a production build: set VITE_SENTRY_DSN (or SENTRY_DSN). This bundle will report no errors.";
}

/**
 * The commit a build was made from, or "" when nothing identifies it.
 *
 * The web bundle is built on two surfaces that name this differently: the
 * desktop release workflows pass VITE_SENTRY_COMMIT_SHA, and Railway injects
 * RAILWAY_GIT_COMMIT_SHA into the cloud shell build. Knowing only the first set
 * is why every Shell build reported the same release.
 *
 * The validity check is shared rather than re-implemented, because a resolver
 * that accepts a placeholder tags a build with a confident, wrong commit.
 */
export function resolveBuildCommitSha(env: Record<string, string | undefined>): string {
  for (const name of ["VITE_SENTRY_COMMIT_SHA", "VITE_COMMIT_SHA"] as const) {
    const value = env[name]?.trim();
    if (isFullCommitSha(value)) return value as string;
  }
  return resolveBuildCommitFromEnv(env) ?? "";
}

/**
 * Two builds that report the same release are one build as far as Sentry is
 * concerned: "first seen in" and "regressed in" both become meaningless, and a
 * fix cannot be told apart from the bug it fixed.
 */
export function describeUnidentifiableProductionBuild(input: {
  readonly commitSha: string;
  readonly mode: string;
}): string | null {
  if (input.mode !== "production") return null;
  if (input.commitSha !== "") return null;
  return "No commit resolved for a production build: set RAILWAY_GIT_COMMIT_SHA (or VITE_SENTRY_COMMIT_SHA). Every build will share one Sentry release, so regression data cannot be trusted.";
}
